import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";
import { Aviso, Botao } from "../componentes/ui";
import { CascaPublica } from "../componentes/layout";
import { FormularioDeNovaSenha } from "../componentes/acesso/FormularioDeNovaSenha";
import { buscarConvite, ErroDaApi } from "../nucleo/api";
import { PassoDoSegundoFator } from "../componentes/acesso/PassoDoSegundoFator";
import type { DesafioDeMfa } from "../nucleo/api";
import { destinoPadrao, ehDesafio, useAceitarConvite, useConcluirEntrada } from "../nucleo/sessao";
import { fragmentosAEvitar } from "../nucleo/senha";
import { useTitulo } from "../nucleo/titulo";

/** Recusa da API ao aceitar o convite, em português e sem vazar detalhe de implementação. */
function mensagemDaRecusa(erro: unknown): string {
  if (erro instanceof ErroDaApi) {
    // A recusa da política de senha chega com o motivo escrito para quem digita (curta,
    // vazada). Trocá-lo por uma frase genérica foi o que fez o defeito 3.5 parecer
    // "o portal recusa qualquer senha": a pessoa nunca ficava sabendo o porquê.
    if (erro.codigo === "regra_de_negocio" && erro.message) {
      return erro.message;
    }
    if (erro.status === 400 || erro.status === 422) {
      return "O portal não aceitou o envio. Confira a senha e tente de novo.";
    }
    if (erro.status === 429) {
      return "Muitas tentativas seguidas. Espere alguns minutos e tente de novo.";
    }
    if (erro.status === null) {
      return "Não foi possível falar com o portal. Verifique a conexão e tente de novo.";
    }
  }
  return "Não foi possível concluir o primeiro acesso agora. Tente de novo em instantes.";
}

function ConviteInvalido() {
  return (
    <CascaPublica
      titulo="Este convite não vale mais"
      descricao="O link de convite tem prazo de validade e serve uma vez só."
      rodape={<Link to="/">Voltar para a página inicial</Link>}
    >
      <div data-testid="convite-invalido">
        <Aviso tom="atencao" className="mb-4">
          <b>Três motivos possíveis, todos com a mesma solução.</b> O prazo do link acabou,
          ele já foi usado para definir uma senha, ou o endereço foi copiado pela metade.
        </Aviso>

        <ul className="acesso__lista">
          <li>
            <b>Se você já definiu a sua senha</b>, o convite era mesmo de uso único — vá
            direto para a tela de entrada.
          </li>
          <li>
            <b>Se ainda não definiu</b>, peça um convite novo a quem enviou o primeiro: o
            consultor da GEOvendas responsável pela implantação ou o administrador do portal
            na sua empresa.
          </li>
          <li>
            <b>Nunca repasse um link de convite</b>: ele é nominal e dá acesso à conta de
            quem o recebeu.
          </li>
        </ul>

        <div className="acesso__acoes">
          <Link to="/entrar" className="botao">
            Ir para a entrada
          </Link>
          <Link to="/recuperar" className="botao botao--fantasma">
            Esqueci a minha senha
          </Link>
        </div>
      </div>
    </CascaPublica>
  );
}

export function Convite() {
  useTitulo("Primeiro acesso");

  const { token = "" } = useParams();
  const navegar = useNavigate();
  const [erroAoAceitar, setErroAoAceitar] = useState<string | null>(null);
  // A organização exige o segundo fator: a senha foi definida, mas a sessão só abre depois dele.
  const [desafio, setDesafio] = useState<DesafioDeMfa | null>(null);
  const concluir = useConcluirEntrada();

  const convite = useQuery({
    queryKey: ["convite", token],
    queryFn: ({ signal }) => buscarConvite(token, { signal }),
    retry: false,
    // Convite é de uso único: uma resposta guardada em cache só serviria para mostrar
    // como válido um token que já foi consumido em outra aba.
    staleTime: 0,
    gcTime: 0,
    enabled: token.length > 0,
  });

  const aceitar = useAceitarConvite(token);

  const evitar = useMemo(
    () => fragmentosAEvitar(convite.data?.email, convite.data?.organizacao),
    [convite.data?.email, convite.data?.organizacao],
  );

  if (token.length === 0 || convite.data === null) return <ConviteInvalido />;

  if (convite.isPending) {
    return (
      <CascaPublica titulo="Verificando o convite…" descricao="Só um instante.">
        <p className="acesso__ajuda m-0" role="status">
          Estamos conferindo se este link ainda é válido.
        </p>
      </CascaPublica>
    );
  }

  if (convite.isError) {
    return (
      <CascaPublica
        titulo="Não deu para verificar o convite"
        descricao="O portal não respondeu. Isso não quer dizer que o seu convite expirou."
        rodape={<Link to="/">Voltar para a página inicial</Link>}
      >
        <Aviso tom="erro" className="mb-4">
          Falha ao falar com o portal. Tente de novo em alguns instantes; se persistir, avise
          quem enviou o convite.
        </Aviso>
        <Botao onClick={() => void convite.refetch()} larguraTotal>
          Tentar de novo
        </Botao>
      </CascaPublica>
    );
  }

  const dados = convite.data;

  if (desafio) {
    return (
      <CascaPublica
        titulo="Configure a verificação em duas etapas"
        descricao="A senha foi definida. Falta configurar o aplicativo autenticador para entrar."
      >
        <PassoDoSegundoFator
          desafio={desafio}
          aoConcluir={() =>
            concluir.mutate(undefined, {
              onSuccess: (sessao) => navegar(destinoPadrao(sessao), { replace: true }),
              onError: () => navegar("/entrar", { replace: true }),
            })
          }
          aoVoltar={() => navegar("/entrar", { replace: true })}
        />
      </CascaPublica>
    );
  }

  return (
    <CascaPublica
      titulo="Defina a sua senha"
      descricao="Este é o seu primeiro acesso ao Portal de Implantação. A senha é sua: a GEOvendas não a vê e nunca vai pedi-la."
      acima={
        <div className="convite-contexto" data-testid="convite-contexto">
          <p className="convite-contexto__linha">
            <span className="convite-contexto__rotulo">Convite para</span>
            <b>{dados.email}</b>
          </p>
          <p className="convite-contexto__linha">
            <span className="convite-contexto__rotulo">Organização</span>
            <b>{dados.organizacao}</b>
          </p>
          <p className="convite-contexto__linha">
            <span className="convite-contexto__rotulo">Perfil</span>
            <b>{dados.papel}</b>
          </p>
        </div>
      }
      rodape={
        <>
          <Link to="/entrar">Já tenho senha, quero entrar</Link>
          <Link to="/">Voltar para a página inicial</Link>
        </>
      }
    >
      <Aviso tom="marca" className="mb-4">
        Confira o e-mail e a organização acima. Se alguma coisa não for a sua, não continue —
        avise quem enviou o convite.
      </Aviso>

      <FormularioDeNovaSenha
        rotuloDoBotao="Definir senha e entrar"
        rotuloEnviando="Definindo…"
        enviando={aceitar.isPending}
        erro={erroAoAceitar}
        evitar={evitar}
        aoEnviar={(senha) => {
          setErroAoAceitar(null);
          aceitar.mutate(senha, {
            onSuccess: (resultado) =>
              ehDesafio(resultado)
                ? setDesafio(resultado)
                : navegar(destinoPadrao(resultado), { replace: true }),
            onError: (erro) => setErroAoAceitar(mensagemDaRecusa(erro)),
          });
        }}
      />
    </CascaPublica>
  );
}
